import { t } from '@framework/i18n/intl/i18next.ts';
import { message } from 'antd';
import { useCallback } from 'react';
import { type UseMutationOptions, useMutation } from 'react-query';
import {
  type IUseFileDownloadApiOptionsParams,
  useFileDownloadApiOptions,
} from '../utils/download/useFileDownloadApiOptions.ts';

function defaultExportOnError(e: any) {
  const status = e?.response?.status ?? e?.status;
  if (status === 404) {
    message.error(t('export.file-non-trovato', 'File non trovato'));
    return;
  }
  message.error(
    t(
      'export.default-errore',
      'Si è verificato un problema durante il download del file. Riprovare.',
    ),
  );
}

/**
 * Estrae il tipo delle variabili di mutazione da una factory di opzioni React Query.
 */
export type GetMutationOptionsVariables<T> = T extends (
  ...input: any[]
) => UseMutationOptions<any, any, infer TVariables>
  ? TVariables
  : never;

/**
 * Parametri di configurazione per {@link useExportFile}.
 *
 * @template TFactory - Tipo della factory che genera le mutation options (es. `typeof getPostBackProcedimentiCsvMutationOptions`)
 */
export interface UseExportFileParams<TFactory extends (...args: any[]) => any> {
  /** Factory che restituisce le opzioni della mutazione React Query per il download. */
  getMutationOptions: TFactory;
  /** Opzioni per il download autenticato (filename, gestione errori, ecc.). */
  downloadApiOptions: IUseFileDownloadApiOptionsParams;
  /**
   * Callback che costruisce le variabili di mutazione a partire dai campi
   * selezionati e dal flag "esporta tutto".
   * Ogni consumer definisce qui la propria logica specifica (filtri, paginazione, ecc.).
   */
  buildVariables: (
    fields: string[],
    exportAll: boolean,
  ) => GetMutationOptionsVariables<TFactory>;
}

/**
 * Risultato restituito da {@link useExportFile}.
 */
export interface UseExportFileResult {
  /** Avvia l'esportazione. Restituisce `true` se il download è riuscito, `false` in caso di errore. */
  onExport: (params: {
    fields: string[];
    exportAll: boolean;
  }) => Promise<boolean>;
}

/**
 * Hook condiviso per l'esportazione di file (CSV, XLSX, GeoJSON, PDF).
 *
 * Funge da ponte tra i consumer (che definiscono *cosa* esportare tramite
 * `buildVariables`) e {@link ExportFileButton} (che gestisce l'UI e
 * invoca `onExport`).
 *
 * Internamente crea una mutation React Query con le opzioni di download
 * autenticato; le variabili vengono costruite al momento dell'export
 * tramite `buildVariables`.
 *
 * In caso di errore, viene invocato `onError` (da `downloadApiOptions`)
 * e `onExport` restituisce `false`, così il chiamante può reagire
 * (es. {@link ExportFileButton} tiene aperta la modale).
 *
 * @example
 * ```tsx
 * const { onExport } = useExportFile({
 *   getMutationOptions: getPostBackProcedimentiCsvMutationOptions,
 *   downloadApiOptions: { filename: 'Procedimenti.csv', onError: ... },
 *   buildVariables: (fields, exportAll) => ({
 *     data: { ...query, visibleFields: fields },
 *   }),
 * });
 * ```
 */
export function useExportFile<TFactory extends (...args: any[]) => any>(
  params: UseExportFileParams<TFactory>,
): UseExportFileResult {
  const { options, onError, onSuccess } = useFileDownloadApiOptions({
    onError: defaultExportOnError,
    ...params.downloadApiOptions,
  });

  const mutationOptions = params.getMutationOptions(options);

  type TVariables = GetMutationOptionsVariables<TFactory>;

  const mutation = useMutation<unknown, unknown, TVariables>({
    mutationKey: mutationOptions.mutationKey,
    mutationFn: mutationOptions.mutationFn,
  });

  const { mutateAsync } = mutation;

  const onExport = useCallback(
    async (args: {
      fields: string[];
      exportAll: boolean;
    }): Promise<boolean> => {
      const variables = params.buildVariables(args.fields, args.exportAll);
      try {
        const data = await mutateAsync(variables);
        onSuccess(data);
        return true;
      } catch (err) {
        onError?.(err);
        return false;
      }
    },
    [mutateAsync, onError, onSuccess, params.buildVariables],
  );

  return { onExport };
}
